import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SortableTable from '../components/SortableTable';
import { useFetch } from '../hooks/useFetchAuth';
import {
  formatDate, formatUnits, formatWeek, formatWeekList, lagStatus, METRIC_LABELS,
} from '../utils/analysisFormat';

const TABS = [
  { key: 'coverage', label: 'Couverture' },
  { key: 'deletions', label: 'Suppressions' },
  { key: 'categories', label: 'Univers' },
  { key: 'same-content', label: 'Contenus identiques' },
];

const TONE_CLASSES = {
  ok: 'text-green-700 dark:text-green-400',
  warn: 'text-yellow-700 dark:text-yellow-400',
  bad: 'text-red-600 dark:text-red-400 font-semibold',
  muted: 'text-gray-400 dark:text-gray-500',
};

const HEATMAP_CLASSES = {
  loaded: 'bg-green-500 dark:bg-green-600',
  missing: 'bg-red-500 dark:bg-red-500',
  outside: 'bg-gray-200 dark:bg-gray-700',
};

const HEATMAP_LABELS = { loaded: 'chargée', missing: 'manquante', outside: 'hors série' };

function Loading({ label }) {
  return (
    <div className="flex justify-center items-center py-8">
      <div className="w-10 h-10 border-4 border-blue-500 dark:border-blue-400 border-t-transparent rounded-full animate-spin"></div>
      <p className="ml-3 text-gray-600 dark:text-gray-300">{label}</p>
    </div>
  );
}

function ErrorMessage({ error }) {
  return <p className="py-6 text-center text-red-500 dark:text-red-400">Erreur : {error?.message || String(error)}</p>;
}

function Intro({ children }) {
  return <p className="mb-4 max-w-4xl text-sm text-gray-600 dark:text-gray-400">{children}</p>;
}

function Select({ label, value, onChange, options }) {
  return (
    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
      {label}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded border border-gray-300 bg-white px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

function Heatmap({ cells }) {
  return (
    <div className="flex gap-0.5">
      {cells.map((cell) => (
        <span
          key={cell.week}
          title={`${formatWeek(cell.week)} : ${HEATMAP_LABELS[cell.status]}`}
          className={`inline-block h-3.5 w-2.5 rounded-sm ${HEATMAP_CLASSES[cell.status]}`}
        />
      ))}
    </div>
  );
}

function CoverageTab() {
  const [byCategory, setByCategory] = useState(true);
  const [onlyIssues, setOnlyIssues] = useState(false);
  const { data, isLoading, error } = useFetch(
    ['analysis', 'coverage', byCategory],
    `/api/analysis/coverage?by_category=${byCategory}`,
  );

  const series = useMemo(() => {
    const all = data?.series || [];
    return onlyIssues ? all.filter((s) => s.missing_weeks.length > 0 || s.lag_weeks > 0) : all;
  }, [data, onlyIssues]);

  const columns = useMemo(() => {
    const cols = [
      { key: 'client', label: 'Client' },
      { key: 'shop_sign', label: 'Enseigne' },
    ];
    if (byCategory) cols.push({ key: 'category', label: 'Univers' });
    cols.push(
      { key: 'last_week', label: 'Dernière semaine', render: (s) => formatWeek(s.last_week) },
      {
        key: 'lag_weeks',
        label: 'Retard',
        render: (s) => {
          const status = lagStatus(s.lag_weeks);
          return <span className={TONE_CLASSES[status.tone]}>{status.label}</span>;
        },
        filterable: false,
      },
      {
        key: 'missing_count',
        label: 'Semaines manquantes',
        rawValue: (s) => s.missing_weeks.length,
        render: (s) => (s.missing_weeks.length === 0 ? <span className="text-gray-400">0</span> : (
          <span title={s.missing_weeks.map(formatWeek).join(', ')} className="text-red-600 dark:text-red-400">
            {s.missing_weeks.length} : {formatWeekList(s.missing_weeks, 4)}
          </span>
        )),
        filterable: false,
        initialWidth: 260,
      },
      { key: 'median_delay_days', label: 'Délai médian (j)', filterable: false },
      { key: 'last_delay_days', label: 'Dernier délai (j)', filterable: false },
      {
        key: 'heatmap',
        label: '26 dernières semaines',
        render: (s) => <Heatmap cells={s.heatmap} />,
        rawValue: () => '',
        filterable: false,
        sortable: false,
        allowOverflow: true,
        initialWidth: 300,
      },
    );
    return cols;
  }, [byCategory]);

  return (
    <div>
      <Intro>
        Une série est une combinaison client × enseigne{byCategory ? ' × univers' : ''}. Les semaines manquantes
        sont celles qui n'ont aucun chargement entre la première et la dernière semaine de la série. Le retard
        compte les semaines entre la dernière semaine chargée et la dernière semaine complète
        ({formatWeek(data?.last_complete_week)}). Le délai est le nombre de jours entre la fin de la semaine
        (dimanche) et son premier chargement.
      </Intro>
      <div className="mb-4 flex flex-wrap items-center gap-6 text-sm text-gray-700 dark:text-gray-200">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={byCategory} onChange={(e) => setByCategory(e.target.checked)} />
          Séparer par univers
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={onlyIssues} onChange={(e) => setOnlyIssues(e.target.checked)} />
          Seulement les séries en retard ou avec des trous
        </label>
        <span className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
          {Object.entries(HEATMAP_LABELS).map(([status, label]) => (
            <span key={status} className="flex items-center gap-1">
              <span className={`inline-block h-3 w-2.5 rounded-sm ${HEATMAP_CLASSES[status]}`} /> {label}
            </span>
          ))}
        </span>
      </div>
      {isLoading && <Loading label="Calcul de la couverture..." />}
      {error && <ErrorMessage error={error} />}
      {!isLoading && !error && (
        <SortableTable
          data={series}
          columns={columns}
          emptyMessage="Aucune série."
          initialSortBy={{ key: 'client', direction: 'ascending' }}
          exportable
          exportFilename="couverture_sellout.csv"
          density="compact"
          maxHeight="65vh"
        />
      )}
    </div>
  );
}

const KIND_LABELS = { deletion: 'Suppression', reprocess: 'Retraitement' };

const RELOADED = {
  same_category: { label: 'Oui', className: 'text-green-700 dark:text-green-400' },
  other_category: { label: 'Sous un autre univers', className: 'text-yellow-700 dark:text-yellow-400' },
  none: { label: 'Non', className: 'text-red-600 dark:text-red-400' },
};

function DeletionsTab() {
  const [days, setDays] = useState('180');
  const { data, isLoading, error } = useFetch(['analysis', 'deletions', days], `/api/analysis/deletions?days=${days}`);
  const loads = useMemo(() => data?.loads || [], [data]);

  const summary = useMemo(() => {
    const byKey = {};
    loads.forEach((l) => {
      const key = `${l.client}|${l.shop_sign}`;
      const row = byKey[key] || (byKey[key] = {
        client: l.client, shop_sign: l.shop_sign, deletion: 0, reprocess: 0, not_reloaded: 0,
      });
      row[l.kind] += 1;
      if (l.kind === 'deletion' && !l.reloaded) row.not_reloaded += 1;
    });
    return Object.values(byKey);
  }, [loads]);

  const summaryColumns = useMemo(() => [
    { key: 'client', label: 'Client' },
    { key: 'shop_sign', label: 'Enseigne' },
    { key: 'deletion', label: 'Suppressions', filterable: false },
    { key: 'reprocess', label: 'Retraitements', filterable: false },
    {
      key: 'not_reloaded',
      label: 'Semaines non rechargées',
      filterable: false,
      render: (r) => <span className={r.not_reloaded > 0 ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-gray-400'}>{r.not_reloaded}</span>,
    },
  ], []);

  const detailColumns = useMemo(() => [
    { key: 'deleted_at', label: 'Supprimé le', render: (l) => formatDate(l.deleted_at), rawValue: (l) => new Date(l.deleted_at).getTime(), filterable: false },
    { key: 'kind', label: 'Type', render: (l) => KIND_LABELS[l.kind], rawValue: (l) => KIND_LABELS[l.kind] },
    { key: 'client', label: 'Client' },
    { key: 'shop_sign', label: 'Enseigne' },
    { key: 'category', label: 'Univers' },
    { key: 'week', label: 'Semaine', render: (l) => formatWeek(l.year * 100 + l.week), rawValue: (l) => l.year * 100 + l.week },
    { key: 'filename', label: 'Fichier', initialWidth: 220 },
    { key: 'sheets', label: 'Onglets', render: (l) => l.sheets.join(', '), rawValue: (l) => l.sheets.join(', '), filterable: false, initialWidth: 200 },
    { key: 'loaded_by', label: 'Chargé par' },
    { key: 'loaded_at', label: 'Chargé le', render: (l) => formatDate(l.loaded_at), rawValue: (l) => new Date(l.loaded_at).getTime(), filterable: false },
    { key: 'deleted_by', label: 'Supprimé par', render: (l) => l.deleted_by || '--' },
    {
      key: 'reloaded',
      label: 'Semaine rechargée',
      render: (l) => {
        const status = RELOADED[l.reloaded || 'none'];
        return <span className={status.className}>{status.label}</span>;
      },
      rawValue: (l) => RELOADED[l.reloaded || 'none'].label,
    },
  ], []);

  return (
    <div>
      <Intro>
        Les chargements supprimés depuis l'historique, et ceux remplacés par un retraitement. « Semaine rechargée »
        indique si la même semaine du même client et de la même enseigne a aujourd'hui un chargement actif, dans
        le même univers ou dans un autre. Seules les semaines sans aucun chargement comptent comme non rechargées.
      </Intro>
      <div className="mb-4">
        <Select
          label="Période"
          value={days}
          onChange={setDays}
          options={[
            { value: '30', label: '30 derniers jours' },
            { value: '90', label: '90 derniers jours' },
            { value: '180', label: '6 derniers mois' },
            { value: '365', label: '12 derniers mois' },
            { value: '3650', label: 'Tout' },
          ]}
        />
      </div>
      {isLoading && <Loading label="Chargement des suppressions..." />}
      {error && <ErrorMessage error={error} />}
      {!isLoading && !error && (
        <div className="flex flex-col gap-6">
          <SortableTable
            data={summary}
            columns={summaryColumns}
            emptyMessage="Aucune suppression sur la période."
            initialSortBy={{ key: 'deletion', direction: 'descending' }}
            density="compact"
          />
          <SortableTable
            data={loads}
            columns={detailColumns}
            emptyMessage="Aucune suppression sur la période."
            initialSortBy={{ key: 'deleted_at', direction: 'descending' }}
            exportable
            exportFilename="suppressions_sellout.csv"
            density="compact"
            maxHeight="55vh"
            pagination
            defaultPageSize={25}
          />
        </div>
      )}
    </div>
  );
}

function CategoriesTab() {
  const { data, isLoading, error } = useFetch(['analysis', 'categories'], '/api/analysis/categories');

  const columns = useMemo(() => [
    { key: 'client', label: 'Client' },
    { key: 'shop_sign', label: 'Enseigne' },
    { key: 'file_template', label: 'Modèle de fichier', initialWidth: 280 },
    {
      key: 'categories',
      label: 'Univers utilisés',
      rawValue: (r) => r.categories.map((c) => c.category).join(', '),
      render: (r) => (
        <div className="flex flex-col gap-0.5">
          {r.categories.map((c) => (
            <span key={c.category}>
              <span className="font-semibold">{c.category}</span> : {c.weeks} sem., de {formatWeek(c.first_week)} à {formatWeek(c.last_week)}
            </span>
          ))}
        </div>
      ),
      filterable: false,
      allowOverflow: true,
      initialWidth: 360,
    },
  ], []);

  return (
    <div>
      <Intro>
        Un même modèle de fichier (le nom sans ses numéros de semaine et d'année) chargé sous plusieurs univers.
        Un changement d'univers dans le temps est souvent un renommage. Deux univers sur les mêmes semaines
        signalent plutôt un fichier chargé deux fois.
      </Intro>
      {isLoading && <Loading label="Analyse des univers..." />}
      {error && <ErrorMessage error={error} />}
      {!isLoading && !error && (
        <SortableTable
          data={data?.conflicts || []}
          columns={columns}
          emptyMessage="Aucun modèle de fichier chargé sous plusieurs univers."
          initialSortBy={{ key: 'client', direction: 'ascending' }}
          exportable
          exportFilename="univers_sellout.csv"
          density="compact"
          wrap
        />
      )}
    </div>
  );
}

const CASE_KIND_LABELS = {
  same_week: 'Même semaine chargée deux fois',
  other_week: 'Copie d\'une autre semaine',
};

function SameContentTab() {
  const navigate = useNavigate();
  const [weeks, setWeeks] = useState('52');
  const { data, isLoading, error } = useFetch(['analysis', 'same-content', weeks], `/api/analysis/same-content?weeks=${weeks}`);

  const columns = useMemo(() => [
    { key: 'client', label: 'Client' },
    {
      key: 'kind',
      label: 'Type',
      render: (c) => (
        <span className={c.kind === 'other_week' ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-yellow-700 dark:text-yellow-400'}>
          {CASE_KIND_LABELS[c.kind]}
        </span>
      ),
      rawValue: (c) => CASE_KIND_LABELS[c.kind],
    },
    { key: 'weeks', label: 'Semaines', render: (c) => c.weeks.map(formatWeek).join(' = '), rawValue: (c) => c.weeks.join(','), filterable: false },
    {
      key: 'loads',
      label: 'Chargements',
      rawValue: (c) => c.loads.map((l) => l.filename || l.folder).join(' | '),
      render: (c) => (
        <div className="flex flex-col gap-1">
          {c.loads.map((l) => (
            <div key={l.folder}>
              <span className="font-medium">{formatWeek(l.year_week)}</span> · {l.shop_sign} · {l.category || '--'} ·{' '}
              {l.uuid ? (
                <button type="button" onClick={() => navigate(`/processor/history/${l.uuid}`)} className="text-blue-600 hover:underline dark:text-blue-400">
                  {l.filename || l.folder}
                </button>
              ) : (
                <span className="text-gray-400" title="Aucune entrée active dans l'historique">{l.folder}</span>
              )}
              <span className="text-gray-500 dark:text-gray-400"> · {l.loaded_by || '?'}, {formatDate(l.created_at)}</span>
            </div>
          ))}
        </div>
      ),
      filterable: false,
      allowOverflow: true,
      initialWidth: 520,
    },
    {
      key: 'tables',
      label: 'Tables identiques',
      rawValue: (c) => c.tables.map((t) => `${t.sheet} / ${METRIC_LABELS[t.metric] || t.metric}`).join(', '),
      render: (c) => (
        <div className="flex flex-col gap-0.5">
          {c.tables.map((t) => (
            <span key={`${t.sheet}-${t.metric}`}>{t.sheet} / {METRIC_LABELS[t.metric] || t.metric} ({formatUnits(t.units)} u.)</span>
          ))}
        </div>
      ),
      filterable: false,
      allowOverflow: true,
      initialWidth: 260,
    },
    { key: 'extra_units', label: 'Unités en double (ventes + stocks)', render: (c) => formatUnits(c.extra_units), filterable: false },
  ], [navigate]);

  return (
    <div>
      <Intro>
        Des chargements dont les lignes (magasin, produit, quantité) sont exactement les mêmes qu'un autre
        chargement du même client. Sur la même semaine, c'est un fichier chargé deux fois. Sur des semaines
        différentes, c'est en général le fichier d'une semaine chargé sous une autre : la vraie semaine manque.
        Il faut supprimer la copie dans l'historique, puis charger le bon fichier.
      </Intro>
      <div className="mb-4">
        <Select
          label="Période"
          value={weeks}
          onChange={setWeeks}
          options={[
            { value: '13', label: '13 dernières semaines' },
            { value: '26', label: '26 dernières semaines' },
            { value: '52', label: '52 dernières semaines' },
            { value: '104', label: '104 dernières semaines' },
          ]}
        />
      </div>
      {isLoading && <Loading label="Comparaison des chargements dans Snowflake..." />}
      {error && <ErrorMessage error={error} />}
      {!isLoading && !error && (
        <SortableTable
          data={data?.cases || []}
          columns={columns}
          emptyMessage="Aucun chargement identique à un autre sur la période."
          initialSortBy={{ key: 'extra_units', direction: 'descending' }}
          exportable
          exportFilename="contenus_identiques_sellout.csv"
          density="compact"
          wrap
        />
      )}
    </div>
  );
}

const TAB_COMPONENTS = {
  coverage: CoverageTab,
  deletions: DeletionsTab,
  categories: CategoriesTab,
  'same-content': SameContentTab,
};

function Analysis() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = TAB_COMPONENTS[searchParams.get('tab')] ? searchParams.get('tab') : 'coverage';
  const ActiveTab = TAB_COMPONENTS[tab];

  return (
    <div className="mx-auto py-8">
      <div className="max-w-[110rem] min-h-[40rem] mx-auto bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="mb-4 flex justify-between items-center">
          <div className="flex flex-col gap-1">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">Analyse des chargements</h2>
            <span className="text-sm text-gray-500 dark:text-gray-400">Limitée aux clients auxquels vous avez accès</span>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 text-sm rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600"
          >
            Retour
          </button>
        </div>

        <div className="mb-5 flex gap-1 border-b border-gray-200 dark:border-gray-700">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setSearchParams({ tab: t.key }, { replace: true })}
              className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${
                tab === t.key
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <ActiveTab />
      </div>
    </div>
  );
}

export default Analysis;
